"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ConstantNode = void 0;
const sheet_db_1 = require("../../sheet_db");
const formula_node_1 = require("./formula.node");
const utils_1 = require("../../utils");
const leaf_node_1 = require("./base/leaf.node");
const antlr_files_1 = require("../antlr_files");
const base_1 = require("./base");
class ConstantNode extends leaf_node_1.LeafNode {
    terminalType;
    constructor(originalNode, parent) {
        super(originalNode, parent);
        this.terminalType = originalNode.symbol.type;
        this._tokenStartPosition = originalNode.symbol.start;
        this._tokenEndPosition = originalNode.symbol.stop + 1;
        this._startPosition = originalNode.symbol.start;
        this._endPosition = originalNode.symbol.stop + 1;
        this._treeNodeType = base_1.TreeNodeType.CONSTANT_NODE;
    }
    accept(v) {
        if (v.visitConstant)
            v.visitConstant(this);
    }
    translate(opts) {
        this.translateValue(opts);
        this.formatText(opts);
        return this.translation;
    }
    translateValue(opts) {
        if (!Number.isNaN(Number(this.token)))
            return;
        // only translate formula arguments
        if (!this.parent || !(this.parent instanceof formula_node_1.FormulaNode))
            return;
        const formulaName = this.parent.formulaName;
        const translation = opts.translations?.formulas[formulaName];
        if (!translation)
            return;
        const formulaDef = sheet_db_1.FormulaLibrary[this.parent.abstractText];
        if (!formulaDef)
            return;
        const argIndex = this.parent.getArgIndex(this);
        const { args_def } = formulaDef;
        let argDef = args_def[argIndex];
        // variadic or more arguments than expected
        if (!argDef)
            argDef = args_def[args_def.length - 1];
        if (!argDef.variadic && argIndex >= args_def.length) {
            // Errors are throw at semantic validation
            return;
        }
        if (argDef.types.includes('date')) {
            this.castDate(translation?.source_date_format ?? '', translation?.target_date_format ?? '', opts);
        }
        const isEnum = argDef.types.find(t => t.startsWith('enum'));
        if (isEnum)
            this.castEnum(translation, argIndex);
    }
    castDate = (sourceDateFormat, targetDateFormat, opts) => {
        const { text, wrapper } = this.getWrapper();
        if (!sourceDateFormat || !targetDateFormat) {
            this.abstractText = `${wrapper}${text}${wrapper}`; // nothing casted, set the original text
            this.translation = `${wrapper}${text}${wrapper}`;
            return;
        }
        const parsedDate = (0, utils_1.doParse)(String(text).trim(), [sourceDateFormat]);
        if (!parsedDate) {
            this.abstractText = `${wrapper}${text}${wrapper}`; // nothing casted, set the original text
            this.translation = `${wrapper}${text}${wrapper}`;
            return;
        }
        const castedDate = (0, utils_1.format)(parsedDate, targetDateFormat);
        const castedStringDate = `${wrapper}${castedDate}${wrapper}`;
        // locale -> YYYY-mm-dd (setting casted date)
        this.abstractText = castedStringDate;
        // YYYY-mm-dd -> locale (setting stored casted date)
        if (opts.isOut)
            this.abstractText = `${wrapper}${text}${wrapper}`; // set the abstract date format
        this.translation = castedStringDate;
    };
    castEnum = (translation, argIndex) => {
        const { text, wrapper } = this.getWrapper();
        const enumTranslator = translation?.replacements?.[argIndex];
        if (enumTranslator) {
            const translatedEnum = enumTranslator?.[(0, utils_1.prepareForComparison)(text).toUpperCase()];
            this._translation = `${wrapper}${text}${wrapper}`;
            if (translatedEnum)
                this._translation = `${wrapper}${translatedEnum}${wrapper}`;
        }
    };
    replaceNormalizer = (argDef, opts) => {
        if (!argDef.replaceNormalizer || opts.isOut)
            return;
        const { text, wrapper } = this.getWrapper();
        const normalizedText = (0, utils_1.normalizeEscapes)(text, argDef.replaceNormalizer.replace, argDef.replaceNormalizer.replace_with);
        this._translation = `${wrapper}${normalizedText}${wrapper}`;
    };
    formatText(opts) {
        if (this.terminalType === antlr_files_1.Lexer.STRING) {
            this.abstractText = (this.abstractText ?? this._translation).slice(1, -1); // set text without quotes
            this._translation = `'${this._translation.slice(1, -1)}'`; // format with single quotes
        }
        if (opts.formatToExecute) {
            const argDef = this.getArgDef();
            if (!argDef?.replaceNormalizer)
                return;
            this.replaceNormalizer(argDef, opts);
        }
    }
    getArgDef() {
        if (!this.parent)
            return;
        if (!(this.parent instanceof formula_node_1.FormulaNode))
            return;
        const formulaDef = sheet_db_1.FormulaLibrary[this.parent.abstractText];
        if (!formulaDef)
            return;
        const argIndex = this.parent.getArgIndex(this);
        const { args_def } = formulaDef;
        let argDef = args_def[argIndex];
        return argDef;
    }
    isRegexPattern() {
        const argDef = this.getArgDef();
        return argDef?.regexPattern ?? false;
    }
    getWrapper() {
        const isString = this.terminalType === antlr_files_1.Lexer.STRING;
        let wrapper = '';
        let text = this.token;
        if (isString) {
            wrapper = this.token[0]; // keep as it received
            text = this.token.slice(1, -1);
        }
        return { text, wrapper };
    }
    getReturnType() {
        if (this.terminalType === antlr_files_1.Parser.BOOL)
            return sheet_db_1.ColumnType.BOOLEAN;
        // TODO: improve number column type recognition
        if (antlr_files_1.Parser.NUMBER_TYPES.has(this.terminalType))
            return sheet_db_1.ColumnType.NUMBER;
        return sheet_db_1.ColumnType.STRING;
    }
    static createConstantNode(text, parent) {
        const newNode = new ConstantNode({
            getText: () => text,
            symbol: { start: 0, stop: 0, type: 0 },
        }, parent);
        return newNode;
    }
}
exports.ConstantNode = ConstantNode;
